import { authAttributeSerializer } from "@megaron/access-control";
import { currencies } from "@megaron/catalog-contracts";
import { dateStringSerializer } from "@megaron/date-string";
import { HttpAction } from "@megaron/http-service";
import { PubsubTopic } from "@megaron/pubsub-contracts";
import { Serializers, SerializerValue } from "@megaron/serializers";
import { uuidSerializer } from "@megaron/uuid";

import { transactionDtoSerializer } from "./transactions";

export const orderStatuses = ["awaiting", "cancelled", "inProgress", "suspended", "fulfilled"] as const;

export type OrderStatus = (typeof orderStatuses)[number];

export const orderLineDtoSerializer = Serializers.object({
  id: uuidSerializer,
  lineNumber: Serializers.integer,
  price: Serializers.decimal,
  quantity: Serializers.decimal,
  itemId: Serializers.string,
  itemName: Serializers.string.nullable(),
  productId: Serializers.string.nullable(),
  productGroup: Serializers.string.nullable(),
  invoiceNumber: Serializers.string.nullable(),
});

export type OrderLineDto = SerializerValue<typeof orderLineDtoSerializer>;

export const orderDtoSerializer = Serializers.object({
  number: Serializers.string,
  type: Serializers.stringOneOf("order", "creditMemo"),
  status: Serializers.stringOneOf(...orderStatuses),
  sellToId: uuidSerializer.nullable(),
  sellToName: Serializers.string.nullable(),
  billToId: uuidSerializer.nullable(),
  billToName: Serializers.string.nullable(),
  orderDate: dateStringSerializer,
  month: Serializers.month,
  createdAt: Serializers.datetime,
  modifiedAt: Serializers.datetime,
  lines: orderLineDtoSerializer.array(),
  netTotal: Serializers.decimal,
  currency: Serializers.stringOneOf(...currencies),
  authorizedAttributes: authAttributeSerializer.array(),
  boostedFor: authAttributeSerializer.array(),
  region: Serializers.string.nullable(),
  regionOwnerEmail: Serializers.string.nullable(),
  draftId: uuidSerializer.nullable(),
  profit: Serializers.decimal,
  promisedDeliveryDate: dateStringSerializer.nullable(),
  ...Serializers.object({
    createdBy: Serializers.string,
  }).partial().fields,
});

export type OrderDto = SerializerValue<typeof orderDtoSerializer>;

const saveOrderDtoSerializer = Serializers.object({
  number: Serializers.string,
  type: Serializers.stringOneOf("order", "creditMemo"),
  status: Serializers.stringOneOf(...orderStatuses),
  sellToId: uuidSerializer,
  billToId: uuidSerializer,
  lines: Serializers.object({
    id: uuidSerializer,
    lineNumber: Serializers.integer,
    itemId: Serializers.string,
    price: Serializers.decimal,
    quantity: Serializers.decimal,
    invoiceNumber: Serializers.string.nullable(),
  }).array(),
  currency: Serializers.stringOneOf(...currencies),
  orderDate: dateStringSerializer,
  draftId: uuidSerializer.nullable(),
  promisedDeliveryDate: dateStringSerializer.nullable(),
});

export type SaveOrderDto = SerializerValue<typeof saveOrderDtoSerializer>;

export const orderActions = {
  refreshBcOrders: HttpAction({
    path: "/orders/refresh",
    method: "post",
    requiresAuth: true as const,
  }),
  refreshBcInvoices: HttpAction({
    path: "/invoices/refresh",
    method: "post",
    requiresAuth: true as const,
  }),
  refreshBcCreditMemos: HttpAction({
    path: "/orders/refreshCreditMemos",
    method: "post",
    requiresAuth: true as const,
  }),
  saveOrder: HttpAction({
    path: "/orders/save",
    method: "post",
    requiresAuth: true as const,
    bodySerializer: saveOrderDtoSerializer,
  }),
  orderQuery: HttpAction({
    path: "/orders/id/:orderNumber",
    method: "get",
    requiresAuth: true as const,
    paramsSerializer: Serializers.object({
      orderNumber: Serializers.string,
    }),
    valueSerializer: orderDtoSerializer,
    errorSerializer: Serializers.stringOneOf("OrderNotFound"),
  }),
  orderBulkQuery: HttpAction({
    path: "/orders/bulkQuery",
    method: "post",
    requiresAuth: true as const,
    bodySerializer: Serializers.object({
      orderNumber: Serializers.string.array(),
      sellToId: uuidSerializer.array(),
      billToId: uuidSerializer.array(),
      region: Serializers.string.array(),
    }).partial(),
    valueSerializer: orderDtoSerializer.array(),
  }),
  recalculateOrderTransactions: HttpAction({
    path: "/orders/recalculateTransactions",
    method: "post",
    requiresAuth: true as const,
  }),
  orderTransactionsPreviewQuery: HttpAction({
    path: "/transactions/orderTransactionsPreview",
    method: "post",
    requiresAuth: true as const,
    bodySerializer: Serializers.object({
      lines: Serializers.object({
        itemId: Serializers.string.optional(),
        itemGtin: Serializers.string.optional(),
        price: Serializers.decimal,
        quantity: Serializers.decimal,
      }).array(),
      billToId: uuidSerializer,
      sellToId: uuidSerializer,
    }),
    valueSerializer: Serializers.object({
      transactions: transactionDtoSerializer.nullable().array(),
      totalProfit: Serializers.decimal,
    }),
  }),

  lockOldOrderTransactions: HttpAction({
    path: "/transactions/lockOldTransactions",
    method: "post",
    requiresAuth: true as const,
    bodySerializer: Serializers.object({
      newCutoff: dateStringSerializer,
    }),
    errorSerializer: Serializers.stringOneOf("DatesAlreadyLocked", "CutoffDateInTheFuture"),
  }),
};

export const ordersChangedTopic = PubsubTopic({
  name: "crm-orders-changed",
  serializer: Serializers.object({
    /** List of changed orders. `undefined` if all orders were changed */
    orders: Serializers.object({
      number: Serializers.string,
      sellToId: uuidSerializer.nullable(),
      billToId: uuidSerializer.nullable(),
    })
      .array()
      .optional(),
  }),
});
